import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import PageShell from "../../components/public/PageShell";
import Pagination from "../../components/public/Pagination";
import { Briefcase, MapPin, Clock, ArrowRight, Suitcase } from "@phosphor-icons/react";
import useReveal from "../../hooks/useReveal";
import usePageMeta from "../../hooks/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PER_PAGE = 8;

export default function CareersList() {
  usePageMeta({
    title: "Careers — Bon-Heur Pharmaceuticals",
    description: "Build the medicines of tomorrow with Bon-Heur Pharmaceuticals. Explore careers across R&D, manufacturing, quality and commercial roles at our Haridwar and Mumbai offices.",
    keywords: "Bon-Heur careers, pharmaceutical jobs Haridwar, pharma R&D jobs, manufacturing jobs India",
  });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dept, setDept] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    axios.get(`${API}/public/careers`).then((r) => setItems(r.data)).catch(() => setItems([])).finally(() => setLoading(false));
  }, []);

  const departments = Array.from(new Set(items.map((i) => i.department).filter(Boolean)));
  const filtered = dept ? items.filter((i) => i.department === dept) : items;
  // Reset to page 1 when the filter changes
  useEffect(() => { setPage(1); }, [dept]);

  return (
    <PageShell pageKey="careers" eyebrow="Careers" title="Build the medicines of tomorrow with us" subtitle="Join a team where rigour, curiosity, and patient impact go hand in hand.">
      <section className="py-14 lg:py-20 bg-white" data-testid="careers-list">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Benefits row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12">
            {[
              { t: "Science-first culture", d: "Cross-functional research and freedom to explore." },
              { t: "Patient-centred mission", d: "Build products that touch lives at scale." },
              { t: "Growth & ownership", d: "Clear career paths and real responsibility from day one." },
            ].map((b, i) => <BenefitTile key={i} b={b} i={i} />)}
          </div>

          {departments.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mb-8" data-testid="careers-filter">
              <button onClick={() => setDept("")} className={`px-4 py-2 rounded-full text-xs font-semibold transition-all ${!dept ? "bg-[#C2410C] text-white" : "bg-[#F1F5F9] text-[#475569] hover:bg-[#E2E8F0]"}`}>All teams</button>
              {departments.map((d) => (
                <button key={d} onClick={() => setDept(d)} className={`px-4 py-2 rounded-full text-xs font-semibold transition-all ${dept === d ? "bg-[#C2410C] text-white" : "bg-[#F1F5F9] text-[#475569] hover:bg-[#E2E8F0]"}`}>{d}</button>
              ))}
            </div>
          )}

          {loading ? (
            <div className="space-y-4">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-24 rounded-2xl bg-[#F1F5F9] animate-pulse" />)}</div>
          ) : filtered.length === 0 ? (
            <div className="py-20 text-center text-[#64748B]"><Suitcase size={36} className="mx-auto text-[#CBD5E1] mb-3" />No open roles match this filter — please check back soon.</div>
          ) : (
            <>
              <div className="space-y-4">
                {filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE).map((c, i) => <JobRow key={c.id} c={c} i={i} />)}
              </div>
              <Pagination
                page={page}
                pageCount={Math.ceil(filtered.length / PER_PAGE)}
                onChange={(p) => { setPage(p); window.scrollTo({ top: 200, behavior: "smooth" }); }}
                testIdPrefix="careers-pagination"
              />
            </>
          )}
        </div>
      </section>
    </PageShell>
  );
}

function BenefitTile({ b, i }) {
  const { ref, visible } = useReveal();
  return (
    <div
      ref={ref}
      className="p-6 rounded-2xl bg-gradient-to-br from-[#F8FAFC] to-white border border-[#E2E8F0] transition-all duration-700"
      style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(20px)", transitionDelay: `${i * 100}ms` }}
    >
      <div className="text-[10px] tracking-[0.3em] uppercase text-[#C2410C] font-semibold">Why Bonheur</div>
      <div className="mt-2 text-base font-semibold text-[#1F2937]" style={{ fontFamily: "'Manrope', sans-serif" }}>{b.t}</div>
      <p className="mt-1.5 text-sm text-[#64748B] leading-relaxed">{b.d}</p>
    </div>
  );
}

function JobRow({ c, i }) {
  const { ref, visible } = useReveal();
  return (
    <div
      ref={ref}
      data-testid={`career-row-${c.slug}`}
      className="transition-all duration-700"
      style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(16px)", transitionDelay: `${(i % 6) * 60}ms` }}
    >
      <Link to={`/careers/${c.slug}`} className="group block p-6 rounded-2xl bg-white border border-[#E2E8F0] hover:border-[#C2410C] hover:shadow-md transition-all duration-300">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <div className="flex flex-wrap items-center gap-3 text-[10px] tracking-[0.25em] uppercase text-[#64748B] font-semibold">
              {c.department && <span className="inline-flex items-center gap-1"><Briefcase size={11} weight="bold" />{c.department}</span>}
              {c.location && <span className="inline-flex items-center gap-1"><MapPin size={11} weight="bold" />{c.location}</span>}
              {c.employment_type && <span className="inline-flex items-center gap-1"><Clock size={11} weight="bold" />{c.employment_type}</span>}
            </div>
            <h3 className="mt-2 text-xl font-bold text-[#1F2937] group-hover:text-[#C2410C] transition-colors" style={{ fontFamily: "'Manrope', sans-serif" }}>{c.title}</h3>
            {c.experience && <p className="mt-1 text-sm text-[#64748B]">Experience: {c.experience}</p>}
          </div>
          <div className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#C2410C]">
            View role <ArrowRight size={14} weight="bold" className="transition-transform group-hover:translate-x-1" />
          </div>
        </div>
      </Link>
    </div>
  );
}
